import * as React from "react";
import {
  ComposerHint,
  CopyMessageButton,
  DaySeparator,
  ListeningWave,
  MessageRegenerate,
  MessageRow,
  MessageTimestamp,
} from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * The six components a transcript needs between its messages, plus every state
 * each of them can be in.
 *
 * All six are presentation only: the calendar, clock, locale, clipboard-policy
 * and send-gating decisions stay with the consumer, which resolves the strings
 * and passes them in. That is what makes them portable across surfaces with
 * different timezone stories and different send gates.
 */

const ANSWER_TEXT =
  "The roadmap is three phases: research, a rough cut, then polish. Start with the rough cut.";

export function TranscriptFurnitureDemo() {
  const [loading, setLoading] = React.useState(false);
  const [regenerating, setRegenerating] = React.useState(false);
  const [copyBusy, setCopyBusy] = React.useState(false);
  const [transcript, setTranscript] = React.useState("");
  const [connecting, setConnecting] = React.useState(true);

  // One cycle: the mic opens (connecting, meter still) -> it is live but has
  // heard nothing ("Listening…") -> words arrive. Loops, so both states are
  // visible without anyone having to click. Deliberately NOT keyed on
  // `connecting`: making the effect depend on the state it sets tears down the
  // pending timers halfway through and the sequence stops meaning what it says.
  React.useEffect(() => {
    const timers: number[] = [];
    const cycle = () => {
      timers.length = 0;
      setConnecting(true);
      setTranscript("");
      timers.push(window.setTimeout(() => setConnecting(false), 1600));
      timers.push(
        window.setTimeout(() => setTranscript("Draft a roadmap for the podcast studio"), 3200),
      );
      timers.push(window.setTimeout(cycle, 6800));
    };
    cycle();
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, []);

  return (
    <Section
      id="transcript-furniture"
      title="Transcript furniture"
      description="DaySeparator, MessageTimestamp, CopyMessageButton, MessageRegenerate, ListeningWave and ComposerHint — the day boundary, the settled-turn actions rail, and the two composer states. Each one draws a hard line between its EMPTY answer (nothing to say, render nothing) and its LOADING answer (something to say, words pending, hold the space); the toggle below walks both."
    >
      <VariantBlock
        label="Loading toggle"
        caption="every piece on this page reads this one flag, so the empty and pending answers sit side by side"
      >
        <button
          type="button"
          onClick={() => setLoading((v) => !v)}
          className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {loading ? "show resolved" : "show loading"}
        </button>
      </VariantBlock>

      <VariantBlock
        label="DaySeparator"
        caption="a real role=separator whose accessible name is the label — a nullish label renders NOTHING, so call sites stay guard-free, while loading holds the bar's geometry"
      >
        <div className="w-full max-w-lg space-y-4">
          <DaySeparator label="Today" />
          <DaySeparator label="Yesterday" />
          <DaySeparator label="24 August 2026" />
          <div>
            <DaySeparator loading={loading} label={loading ? null : "Last week"} />
            <p className="mt-1 text-xs italic text-muted-foreground">
              {loading ? "loading — a boundary whose words have not arrived" : "resolved"}
            </p>
          </div>
          <div>
            <DaySeparator label={null} />
            <p className="text-xs italic text-muted-foreground">
              label={"{null}"} — renders nothing at all. "No boundary here."
            </p>
          </div>
        </div>
      </VariantBlock>

      <VariantBlock
        label="MessageTimestamp"
        caption="a real <time dateTime> carrying the absolute moment on BOTH title and aria-label — title alone puts the exact time behind a hover, which is unreachable on touch"
      >
        <div className="w-full max-w-lg space-y-3">
          <div className="flex items-center gap-4">
            <MessageTimestamp
              dateTime="2026-08-25T09:14:00.000Z"
              label="9:14 AM"
              absoluteLabel="August 25, 2026 at 9:14 AM"
            />
            <MessageTimestamp
              dateTime="2026-08-24T22:03:00.000Z"
              label="Yesterday"
              absoluteLabel="August 24, 2026 at 10:03 PM"
            />
            <MessageTimestamp
              loading={loading}
              dateTime="2026-08-20T11:00:00.000Z"
              label="5d ago"
            />
          </div>
          <p className="text-xs italic text-muted-foreground">
            Hover any of them for the absolute moment. Without both a dateTime and a label it
            renders nothing, so a still-streaming turn leaves no ghost row.
          </p>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Actions rail"
        caption="CopyMessageButton writes BOTH clipboard flavours; MessageRegenerate is a sibling of MessageRetry, not a variant — retry belongs to a user message that never landed, regenerate to an assistant message that landed fine"
      >
        <div className="w-full max-w-xl">
          <MessageRow
            role="assistant"
            actionsVisibility="always"
            actions={
              <>
                <CopyMessageButton
                  text={ANSWER_TEXT}
                  // A thunk, not a string: rendering an answer to HTML is real
                  // work and most messages are never copied, so it runs on
                  // click and nowhere else.
                  html={() => `<p>${ANSWER_TEXT}</p>`}
                  isCopying={copyBusy}
                  onCopied={() => {
                    setCopyBusy(true);
                    window.setTimeout(() => setCopyBusy(false), 900);
                  }}
                />
                <MessageRegenerate
                  isRegenerating={regenerating}
                  onRegenerate={() => {
                    setRegenerating(true);
                    window.setTimeout(() => setRegenerating(false), 1400);
                  }}
                />
              </>
            }
            timestamp={
              <MessageTimestamp
                dateTime="2026-08-25T09:14:00.000Z"
                label="9:14 AM"
                absoluteLabel="August 25, 2026 at 9:14 AM"
              />
            }
          >
            {ANSWER_TEXT}
          </MessageRow>
          <p className="mt-2 text-xs italic text-muted-foreground">
            Click either. Both swap to a Spinner, force-disable and set aria-busy, so the same turn
            cannot be queued twice.
          </p>
        </div>
      </VariantBlock>

      <VariantBlock
        label="ListeningWave"
        caption="the in-composer recording state — the in-place twin of ListeningOverlay. The meter runs on the preset's voicebar keyframe, which the reduced-motion plugin registers"
      >
        <div className="w-full max-w-xl space-y-3">
          <ListeningWave
            connecting={connecting}
            transcript={transcript}
            maxHeight={96}
            onStop={() => {}}
          />
          <p className="text-xs italic text-muted-foreground">
            {connecting
              ? "connecting — the meter holds STILL. An animated meter with no audio behind it is a lie about what the microphone is doing."
              : transcript
                ? "live transcript"
                : 'mic is open and nothing has been heard yet, so "Listening…" is a true statement'}
            {" · loops every few seconds"}
          </p>
        </div>
      </VariantBlock>

      <VariantBlock
        label="ComposerHint"
        caption="the muted status line that explains a gated send in words, because a disabled control cannot explain itself. It keeps its live region MOUNTED and toggles only the content — a region injected together with its text is frequently missed by screen readers"
      >
        <div className="w-full max-w-xl space-y-3 rounded-2xl border border-border bg-card p-4">
          <ComposerHint>Shift + Enter for a new line.</ComposerHint>
          <ComposerHint tone="pending">Atlas is still finishing the last answer.</ComposerHint>
          <ComposerHint tone="warning">Attach a file under 20 MB to send.</ComposerHint>
          <ComposerHint tone="danger">You are out of credits for this studio.</ComposerHint>
          <ComposerHint dot={false} srOnlyText="Message is too long to send.">
            2,048 / 2,000 characters.
          </ComposerHint>
          <div>
            <ComposerHint loading={loading}>{loading ? null : "Ready when you are."}</ComposerHint>
            <p className="mt-1 text-xs italic text-muted-foreground">
              {loading
                ? "loading — a placeholder that stays SILENT to assistive tech. A live region that fires on “loading” and again on the real copy announces twice, and the first time says nothing useful."
                : "resolved"}
            </p>
          </div>
          <div>
            <ComposerHint>{null}</ComposerHint>
            <p className="text-xs italic text-muted-foreground">
              Nullish children — nothing visible, but the live region STAYS MOUNTED. That is the
              whole reason this is a component and not a conditional at each call site.
            </p>
          </div>
        </div>
      </VariantBlock>
    </Section>
  );
}
